import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { useQueryClient } from "@tanstack/react-query";
import type { ThemeVersion } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateAdminThemes } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type UpdateThemeVersionJsonInput = {
  themeId: string;
  versionId: string;
  schemaVersion: number;
  settings: Record<string, unknown>;
  templates: Record<string, unknown>;
  pages?: Record<string, unknown>;
};

export function useUpdateThemeVersionJsonMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ themeId, versionId, ...body }: UpdateThemeVersionJsonInput) =>
      apiJsonClient<ThemeVersion>(
        endpoints.admin.themeVersion(themeId, versionId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateAdminThemes(queryClient);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.admin.themeById(variables.themeId),
        exact: true,
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
